import { useEffect, useRef, useState, useCallback } from "react";
import "./App.css";
import { exportComponentAsPNG } from "react-component-export-image";
import useThrottle from "./hooks/useThrottle";
import useDebounce from "./hooks/useDebounce";
import { Helmet, HelmetProvider } from "react-helmet-async";
import OpenAI from "openai";
import { ReactComponent as GuidelinesSVG } from "./guidelines.svg";
import {
  trackModeChange,
  trackEmojiInput,
  trackRandomizeEmoji,
  trackColorChange,
  trackTextareaInput,
  trackImageSizeChange,
  trackPasteImage,
  trackDownload,
  trackAiGeneration,
} from "./analytics";

const packageJson = require("./../package.json");

const TRACKING_ID = process.env.REACT_APP_GA_ID;

type BackgroundRemovalStrategy = "global-color-match" | "edge-connected";

const isMobileDevice = (): boolean => {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
};

const getOpenAiErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "Unknown error";
};

const getRandomEmoji = (): string => {
  const emojiCodePoint =
    Math.floor(Math.random() * (0x1f64f - 0x1f600 + 1)) + 0x1f600;

  const emoji = String.fromCodePoint(emojiCodePoint);

  return emoji;
};

const COOL_COLORS = [
  "#F8F2D9",
  "#F7F1D8",
  "#F8F1DF",
  "#F7F0E7",
  "#F6E5CA",
  "#F9E6D1",
  "#F2E3DF",
  "#F5E1E2",
  "#F8DBE1",
  "#F5E4E4",
  "#EFDAE5",
  "#EDE0EC",
  "#DEE6EE",
  "#D5E5F0",
  "#D9E9EB",
  "#E9EDE8",
  "#E3EFD9",
  "#EBF0E7",
  "#F7F5E0",
  "#F2F1E6",
  "#F3EAC6",
  "#F3EBC7",
  "#F4E8D8",
  "#F8E0C7",
  "#F7E8D8",
  "#F7DED6",
  "#F1E6E6",
  "#E6D1DA",
  "#DAE6EF",
  "#E8EAEB",
];

const getRandomColor = (): string => {
  return COOL_COLORS[Math.floor(Math.random() * COOL_COLORS.length)];
};

const getViewport = (): number => {
  return Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0,
  );
};

function App() {
  const [bgColor, setBgColor] = useState(getRandomColor());
  const [emoji, setEmoji] = useState(getRandomEmoji());
  const [canvasDistanceToTop, setCanvasDistanceToTop] = useState(0);
  const [scrollAmount, setScrollAmount] = useState(window.scrollY);
  const [viewPort, setViewport] = useState(getViewport());

  const [mode, setMode] = useState<"standard" | "ai" | "paste">("standard");
  const [openAiApiKey, setOpenAiApiKey] = useState(() => {
    return localStorage.getItem("openai-api-key") || "";
  });
  const [emojiDescription, setEmojiDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  );
  const [imageSize, setImageSize] = useState(100);
  const [pastedImageUrl, setPastedImageUrl] = useState<string | null>(null);
  const [originalPastedImageUrl, setOriginalPastedImageUrl] = useState<
    string | null
  >(null);
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);
  const [removePasteBackground, setRemovePasteBackground] = useState(false);
  const [backgroundTolerance, setBackgroundTolerance] = useState(30);
  const [backgroundRemovalStrategy, setBackgroundRemovalStrategy] =
    useState<BackgroundRemovalStrategy>("global-color-match");
  const [mobileExportImageUrl, setMobileExportImageUrl] = useState<
    string | null
  >(null);
  const [isPreparingMobileExport, setIsPreparingMobileExport] =
    useState(false);

  const throttledScrollAmount = useThrottle(scrollAmount);
  const throttledViewport = useThrottle(viewPort);
  const debouncedEmojiDescription = useDebounce(emojiDescription, 1000);

  const downloadRef = useRef<HTMLDivElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const html2CanvasOptions = {
    y: canvasDistanceToTop + scrollAmount,
    useCORS: true,
    allowTaint: true,
    backgroundColor: null,
  };

  const handleDownloadImage = async () => {
    const hasCustomizations =
      (mode === "ai" &&
        (emojiDescription.trim() !== "" || imageSize !== 100)) ||
      (mode === "paste" && imageSize !== 100) ||
      (mode === "standard" && emoji !== getRandomEmoji()) ||
      bgColor !== getRandomColor();

    trackDownload(mode, hasCustomizations);

    if (mode === "ai" && generatedImageUrl) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (mode === "paste" && pastedImageUrl) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (isMobileDevice() && downloadRef.current) {
      setIsPreparingMobileExport(true);

      try {
        const preview = downloadRef.current;
        const content = preview.firstElementChild as HTMLElement | null;
        if (!content) {
          throw new Error("No image content available to export");
        }

        const previewBounds = preview.getBoundingClientRect();
        const scale = window.devicePixelRatio || 1;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(previewBounds.width * scale);
        canvas.height = Math.round(previewBounds.height * scale);
        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Unable to create export canvas");
        }

        context.fillStyle = bgColor;
        context.fillRect(0, 0, canvas.width, canvas.height);

        if (content instanceof HTMLImageElement) {
          const contentBounds = content.getBoundingClientRect();
          const imageWidth = Math.round(contentBounds.width * scale);
          const imageHeight = Math.round(contentBounds.height * scale);

          context.drawImage(
            content,
            (canvas.width - imageWidth) / 2,
            (canvas.height - imageHeight) / 2,
            imageWidth,
            imageHeight,
          );
        } else {
          const contentStyles = window.getComputedStyle(content);
          const fontSize = parseFloat(contentStyles.fontSize) * scale;
          const emoji = content.textContent ?? "";

          context.font = `${contentStyles.fontStyle} ${contentStyles.fontWeight} ${fontSize}px ${contentStyles.fontFamily}`;
          context.fillStyle = contentStyles.color;
          context.textAlign = "center";
          context.textBaseline = "alphabetic";

          const metrics = context.measureText(emoji);
          const baseline =
            canvas.height / 2 +
            (metrics.actualBoundingBoxAscent -
              metrics.actualBoundingBoxDescent) /
              2;
          context.fillText(emoji, canvas.width / 2, baseline);
        }

        const imageBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Unable to create PNG image"));
            }
          }, "image/png");
        });

        setMobileExportImageUrl((previousImageUrl) => {
          if (previousImageUrl) {
            URL.revokeObjectURL(previousImageUrl);
          }
          return URL.createObjectURL(imageBlob);
        });
        return;
      } catch (error) {
        console.error("Unable to prepare image for mobile export", error);
        return;
      } finally {
        setIsPreparingMobileExport(false);
      }
    }

    exportComponentAsPNG(downloadRef, {
      fileName: "emojipic.png",
      html2CanvasOptions,
    });
  };

  const handlePaste = useCallback(
    async (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;
      if (mode !== "paste") return;

      const items = clipboardEvent.clipboardData?.items;
      if (!items) {
        trackPasteImage(false);
        return;
      }

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf("image") !== -1) {
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = async (e) => {
              const result = e.target?.result;
              if (typeof result === "string") {
                setOriginalPastedImageUrl(result);
                const finalImageUrl = removePasteBackground
                  ? await removeBackground(
                      result,
                      backgroundTolerance,
                      backgroundRemovalStrategy,
                    )
                  : result;
                setPastedImageUrl(finalImageUrl);
                trackPasteImage(true, item.type);
              }
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    },
    [mode, removePasteBackground, backgroundTolerance, backgroundRemovalStrategy],
  );

  const handleHiddenInputPaste = async (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    if (mode !== "paste") return;

    const items = event.clipboardData?.items;
    if (!items) {
      trackPasteImage(false);
      return;
    }

    event.preventDefault();

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = async (e) => {
            const result = e.target?.result;
            if (typeof result === "string") {
              setOriginalPastedImageUrl(result);
              const finalImageUrl = removePasteBackground
                ? await removeBackground(
                    result,
                    backgroundTolerance,
                    backgroundRemovalStrategy,
                  )
                : result;
              setPastedImageUrl(finalImageUrl);
              trackPasteImage(true, item.type);
            }
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }

    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = "";
      hiddenInputRef.current.blur();
    }
  };

  const updateDistanceToTop = () => {
    const canvaselement = downloadRef.current;
    const distanceToTop = canvaselement?.getBoundingClientRect().top ?? 0;
    setCanvasDistanceToTop(distanceToTop);
  };

  useEffect(() => {
    updateDistanceToTop();
  }, [throttledScrollAmount]);

  useEffect(() => {
    updateDistanceToTop();
  }, [throttledViewport]);

  useEffect(() => {
    updateDistanceToTop();
  }, []);

  const resizeHandler = () => {
    setViewport(getViewport());
  };

  const scrollHandler = () => {
    setScrollAmount(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener("resize", resizeHandler, false);
    window.addEventListener("scroll", scrollHandler, false);
    window.addEventListener("paste", handlePaste, false);

    return () => {
      window.removeEventListener("resize", resizeHandler, false);
      window.removeEventListener("scroll", scrollHandler, false);
      window.removeEventListener("paste", handlePaste, false);
    };
  }, [mode, handlePaste]);

  useEffect(() => {
    if (mode !== "paste") {
      setPastedImageUrl(null);
      setOriginalPastedImageUrl(null);
    }
    if (mode !== "ai") {
      setGeneratedImageUrl(null);
    }
  }, [mode]);

  useEffect(() => {
    if (openAiApiKey) {
      localStorage.setItem("openai-api-key", openAiApiKey);
    }
  }, [openAiApiKey]);

  useEffect(() => {
    if (mode === "ai" && debouncedEmojiDescription !== undefined) {
      trackTextareaInput(
        debouncedEmojiDescription.length,
        debouncedEmojiDescription.length > 0,
        debouncedEmojiDescription,
      );
    }
  }, [debouncedEmojiDescription, mode]);

  useEffect(() => {
    const applyBackgroundRemoval = async () => {
      if (mode === "paste" && originalPastedImageUrl) {
        try {
          if (removePasteBackground) {
            const processedUrl = await removeBackground(
              originalPastedImageUrl,
              backgroundTolerance,
              backgroundRemovalStrategy,
            );
            setPastedImageUrl(processedUrl);
          } else {
            setPastedImageUrl(originalPastedImageUrl);
          }
        } catch (error) {
          console.error("Error removing background:", error);
        }
      }
    };

    applyBackgroundRemoval();
  }, [
    removePasteBackground,
    mode,
    originalPastedImageUrl,
    backgroundTolerance,
    backgroundRemovalStrategy,
  ]);

  const createAntiShadowPrompt = (description: string): string => {
    return `Apple style emoji of: "${description}". Clean white background.`;
  };

  const removeBackground = (
    imageDataUrl: string,
    tolerance: number = backgroundTolerance,
    strategy: BackgroundRemovalStrategy = backgroundRemovalStrategy,
  ): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d")!;

        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        const corners = [
          { x: 0, y: 0 },
          { x: canvas.width - 1, y: 0 },
          { x: 0, y: canvas.height - 1 },
          { x: canvas.width - 1, y: canvas.height - 1 },
        ];

        const cornerColors: { [key: string]: number } = {};
        corners.forEach((corner) => {
          const index = (corner.y * canvas.width + corner.x) * 4;
          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];
          const colorKey = `${r},${g},${b}`;
          cornerColors[colorKey] = (cornerColors[colorKey] || 0) + 1;
        });

        const bgColor = Object.entries(cornerColors)
          .sort(([, a], [, b]) => b - a)[0][0]
          .split(",")
          .map(Number);

        const isBackgroundColor = (pixelIndex: number): boolean => {
          const index = pixelIndex * 4;
          const dr = Math.abs(data[index] - bgColor[0]);
          const dg = Math.abs(data[index + 1] - bgColor[1]);
          const db = Math.abs(data[index + 2] - bgColor[2]);

          return dr < tolerance && dg < tolerance && db < tolerance;
        };

        if (strategy === "global-color-match") {
          for (let pixelIndex = 0; pixelIndex < canvas.width * canvas.height; pixelIndex++) {
            if (isBackgroundColor(pixelIndex)) {
              data[pixelIndex * 4 + 3] = 0;
            }
          }
        } else {
          // Only remove matching pixels that are connected to the image edge.
          // Matching colours enclosed by the subject are deliberately preserved.
          const pixelCount = canvas.width * canvas.height;
          const visited = new Uint8Array(pixelCount);
          const queue = new Uint32Array(pixelCount);
          let queueStart = 0;
          let queueEnd = 0;

          const enqueueIfBackground = (pixelIndex: number) => {
            if (!visited[pixelIndex] && isBackgroundColor(pixelIndex)) {
              visited[pixelIndex] = 1;
              queue[queueEnd++] = pixelIndex;
            }
          };

          for (let x = 0; x < canvas.width; x++) {
            enqueueIfBackground(x);
            enqueueIfBackground((canvas.height - 1) * canvas.width + x);
          }
          for (let y = 1; y < canvas.height - 1; y++) {
            enqueueIfBackground(y * canvas.width);
            enqueueIfBackground(y * canvas.width + canvas.width - 1);
          }

          while (queueStart < queueEnd) {
            const pixelIndex = queue[queueStart++];
            const x = pixelIndex % canvas.width;
            const y = Math.floor(pixelIndex / canvas.width);

            data[pixelIndex * 4 + 3] = 0;

            if (x > 0) enqueueIfBackground(pixelIndex - 1);
            if (x < canvas.width - 1) enqueueIfBackground(pixelIndex + 1);
            if (y > 0) enqueueIfBackground(pixelIndex - canvas.width);
            if (y < canvas.height - 1) {
              enqueueIfBackground(pixelIndex + canvas.width);
            }
          }
        }

        ctx.putImageData(imageData, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      };
      img.crossOrigin = "anonymous";
      img.src = imageDataUrl;
    });
  };

  const generateAiEmoji = async () => {
    setIsGenerating(true);

    trackAiGeneration("start", emojiDescription.length);

    try {
      const openai = new OpenAI({
        apiKey: openAiApiKey,
        dangerouslyAllowBrowser: true,
      });

      const enhancedPrompt = createAntiShadowPrompt(emojiDescription);

      const response = await openai.images.generate({
        model: "gpt-image-1",
        prompt: enhancedPrompt,
        n: 1,
        size: "1024x1024",
        background: "transparent",
        output_format: "png",
      });

      const b64 = response.data?.[0]?.b64_json;
      if (b64) {
        const dataUrl = `data:image/png;base64,${b64}`;

        const transparentDataUrl = await removeBackground(
          dataUrl,
          backgroundTolerance,
          backgroundRemovalStrategy,
        );
        setGeneratedImageUrl(transparentDataUrl);

        trackAiGeneration("success", emojiDescription.length);
      } else {
        throw new Error("No base64 image received from OpenAI");
      }
    } catch (error) {
      console.error("Error generating AI emoji:", error);

      const errorMessage = getOpenAiErrorMessage(error);
      trackAiGeneration("error", emojiDescription.length, errorMessage);

      alert(`Error generating emoji: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <HelmetProvider>
      <Helmet>
        <script
          src={`https://www.googletagmanager.com/gtag/js?id=${TRACKING_ID}`}
          async
        ></script>
        <script>
          {`window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${TRACKING_ID}');`}
        </script>
      </Helmet>
      <div className="shadow-lg mt-4 lg:mt-8 p-4 sm:p-8 bg-slate-50 max-w-6xl ml-4 mr-4 lg:ml-auto lg:mr-auto rounded-lg">
        <h1 id="appTitle" className="text-4xl font-bold mb-4">
          Emojipic
        </h1>

        <div className="mb-6">
          <label className="font-bold block mb-2">Mode:</label>

          <div className="relative inline-block w-64">
            <select
              value={mode}
              onChange={(e) => {
                const newMode = e.target.value as "standard" | "ai" | "paste";
                trackModeChange(newMode, mode);
                setMode(newMode);
              }}
              style={{ WebkitAppearance: "none", MozAppearance: "none" }}
              className="appearance-none block w-full bg-gray-200 border-2 border-gray-200 rounded py-2 pl-4 pr-10 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-sky-500 text-md font-regular"
              aria-label="Mode"
            >
              <option value="standard">Standard Mode</option>
              <option value="ai">AI Mode</option>
              <option value="paste">Paste Mode</option>
            </select>

            {/* custom arrow */}
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 8l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <div className="flex justify-between mt-0 sm:mt-6 flex-col lg:flex-row">
          <div className="w-full">
            {mode === "ai" ? (
              <>
                <label className="font-bold block">OpenAI API Key:</label>
                <input
                  type="password"
                  value={openAiApiKey}
                  onChange={(e) => setOpenAiApiKey(e.target.value)}
                  placeholder="Enter your OpenAI API key"
                  className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full max-w-sm py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-sky-500 mb-4"
                />

                <label className="font-bold block">Describe your emoji:</label>
                <textarea
                  value={emojiDescription}
                  onChange={(e) => setEmojiDescription(e.target.value)}
                  placeholder="Describe the emoji you want to generate (e.g., a happy cat wearing sunglasses)"
                  rows={3}
                  className="bg-gray-200 h-32 appearance-none border-2 border-gray-200 rounded w-full max-w-sm py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-sky-500 mb-4"
                />

                <label className="font-bold block mb-2">
                  Image Size: {imageSize}%
                </label>
                <input
                  disabled={generatedImageUrl === null}
                  id="image-size-range"
                  type="range"
                  min="10"
                  max="100"
                  step="1"
                  value={imageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setImageSize(newSize);
                    trackImageSizeChange(newSize, "ai", "change");
                  }}
                  onMouseDown={
                    generatedImageUrl
                      ? () => {
                          setIsDraggingSlider(true);
                          trackImageSizeChange(imageSize, "ai", "start");
                        }
                      : undefined
                  }
                  onMouseUp={
                    generatedImageUrl
                      ? () => {
                          setIsDraggingSlider(false);
                          trackImageSizeChange(imageSize, "ai", "end");
                        }
                      : undefined
                  }
                  onTouchStart={
                    generatedImageUrl
                      ? () => {
                          setIsDraggingSlider(true);
                          trackImageSizeChange(imageSize, "ai", "start");
                        }
                      : undefined
                  }
                  onTouchEnd={
                    generatedImageUrl
                      ? () => {
                          setIsDraggingSlider(false);
                          trackImageSizeChange(imageSize, "ai", "end");
                        }
                      : undefined
                  }
                  className="w-full max-w-sm h-2 mb-4 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />

                <button
                  onClick={generateAiEmoji}
                  disabled={
                    isGenerating ||
                    !openAiApiKey.trim() ||
                    !emojiDescription.trim()
                  }
                  className="bg-blue-500 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded block mb-4"
                >
                  {isGenerating ? "Generating..." : "Generate"}
                </button>

                <br />
              </>
            ) : mode === "paste" ? (
              <>
                <label className="font-bold block mb-2">
                  Image Size: {imageSize}%
                </label>
                <input
                  disabled={pastedImageUrl === null}
                  id="image-size-range"
                  type="range"
                  min="10"
                  max="100"
                  step="1"
                  value={imageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setImageSize(newSize);
                    trackImageSizeChange(newSize, "paste", "change");
                  }}
                  onMouseDown={
                    pastedImageUrl
                      ? () => {
                          setIsDraggingSlider(true);
                          trackImageSizeChange(imageSize, "paste", "start");
                        }
                      : undefined
                  }
                  onMouseUp={
                    pastedImageUrl
                      ? () => {
                          setIsDraggingSlider(false);
                          trackImageSizeChange(imageSize, "paste", "end");
                        }
                      : undefined
                  }
                  onTouchStart={
                    pastedImageUrl
                      ? () => {
                          setIsDraggingSlider(true);
                          trackImageSizeChange(imageSize, "paste", "start");
                        }
                      : undefined
                  }
                  onTouchEnd={
                    pastedImageUrl
                      ? () => {
                          setIsDraggingSlider(false);
                          trackImageSizeChange(imageSize, "paste", "end");
                        }
                      : undefined
                  }
                  className="w-full max-w-sm h-2 mb-4 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />

                <br />
                <br />

                <label className="font-bold block mb-2">
                  Background Removal:
                </label>

                <label className="flex items-center max-w-sm mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={removePasteBackground}
                    onChange={(e) => setRemovePasteBackground(e.target.checked)}
                    className="w-6 h-6 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="ml-2 text-md font-medium text-gray-700">
                    Remove Image Background
                  </span>
                </label>

                {removePasteBackground && (
                  <>
                    <fieldset className="max-w-sm mb-4">
                      <legend className="font-bold mb-2">
                        Background Removal Strategy:
                      </legend>
                      <label className="flex items-center mb-2 cursor-pointer">
                        <input
                          type="radio"
                          name="background-removal-strategy"
                          value="global-color-match"
                          checked={
                            backgroundRemovalStrategy === "global-color-match"
                          }
                          onChange={() =>
                            setBackgroundRemovalStrategy("global-color-match")
                          }
                          className="w-4 h-4 bg-gray-100 border-gray-300 focus:outline-none focus:ring-0"
                        />
                        <span className="ml-2 text-md font-medium text-gray-700">
                          Global color match
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="background-removal-strategy"
                          value="edge-connected"
                          checked={
                            backgroundRemovalStrategy === "edge-connected"
                          }
                          onChange={() =>
                            setBackgroundRemovalStrategy("edge-connected")
                          }
                          className="w-4 h-4 bg-gray-100 border-gray-300 focus:outline-none focus:ring-0"
                        />
                        <span className="ml-2 text-md font-medium text-gray-700">
                          Edge connected
                        </span>
                      </label>
                    </fieldset>
                    <label
                      htmlFor="background-tolerance-range"
                      className="font-bold block mb-2"
                    >
                      Background Tolerance: {backgroundTolerance}
                    </label>
                    <input
                      id="background-tolerance-range"
                      type="range"
                      min="0"
                      max="255"
                      step="1"
                      value={backgroundTolerance}
                      onChange={(e) =>
                        setBackgroundTolerance(Number(e.target.value))
                      }
                      className="w-full max-w-sm h-2 mb-4 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                    />
                  </>
                )}

                <br />
              </>
            ) : (
              <>
                <label className="font-bold block">Emoji:</label>
                <input
                  type="text"
                  value={emoji}
                  onChange={(e) => {
                    const newEmoji = e.target.value;
                    setEmoji(newEmoji);
                    if (newEmoji.trim()) {
                      trackEmojiInput(newEmoji, "typing");
                    }
                  }}
                  size={1}
                  maxLength={2}
                  className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-40 py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-sky-500 text-2xl"
                />

                <button
                  className="font-medium text-blue-600 dark:text-blue-500 hover:underline block"
                  onClick={() => {
                    const newEmoji = getRandomEmoji();
                    setEmoji(newEmoji);
                    trackRandomizeEmoji(newEmoji);
                  }}
                >
                  Randomize emoji
                </button>

                <br />
              </>
            )}

            <label className="font-bold block">Background color:</label>
            <input
              type="text"
              value={bgColor}
              onChange={(e) => {
                const newColor = e.target.value;
                setBgColor(newColor);
                trackColorChange(newColor, "text_input");
              }}
              size={1}
              className="bg-gray-200 inline-block appearance-none border-2 border-gray-200 rounded w-40 py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-sky-500 text-2xl align-bottom"
            />

            <input
              type="color"
              value={bgColor}
              onChange={(e) => {
                const newColor = e.target.value;
                setBgColor(newColor);
                trackColorChange(newColor, "color_picker");
              }}
              className="ml-2 h-12 w-12"
            />

            <button
              className="font-medium text-blue-600 dark:text-blue-500 hover:underline block"
              onClick={() => {
                const newColor = getRandomColor();
                setBgColor(newColor);
                trackColorChange(newColor, "random");
              }}
            >
              Randomize color
            </button>
          </div>
          <div>
            <div className="relative">
              <div className="absolute lg:h-[600px] w-full h-[200px] flex items-center justify-center mt-0 ml-auto mr-auto select-none pointer-events-none">
                <div
                  className="lg:w-[600px] lg:h-[600px] w-[200px] h-[200px]"
                  style={{
                    backgroundColor: bgColor,
                    zIndex: 1,
                    display:
                      (mode === "ai" || mode === "paste") && isDraggingSlider
                        ? "block"
                        : "none",
                  }}
                >
                  <GuidelinesSVG className="lg:w-[600px] lg:h-[600px] w-[200px] h-[200px]" />
                </div>
              </div>

              <div
                ref={downloadRef}
                className="lg:w-[600px] lg:h-[600px] w-[200px] h-[200px] flex items-center justify-center mt-8 lg:mt-0 ml-auto mr-auto select-none"
                style={{
                  backgroundColor: bgColor,
                  opacity:
                    (mode === "ai" || mode === "paste") && isDraggingSlider
                      ? 0.8
                      : 1,
                  transition: "opacity 0.2s ease-in-out",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {mode === "ai" ? (
                  isGenerating ? (
                    <span className="text-9xl bounce-animation">⌛</span>
                  ) : generatedImageUrl ? (
                    <img
                      crossOrigin="anonymous"
                      src={generatedImageUrl}
                      alt="AI Generated Emoji"
                      className="object-contain"
                      style={{
                        width: `${imageSize}%`,
                        maxWidth: "100%",
                        maxHeight: "100%",
                      }}
                    />
                  ) : null
                ) : mode === "paste" ? (
                  pastedImageUrl ? (
                    <img
                      onClick={() => setPastedImageUrl(null)}
                      src={pastedImageUrl}
                      alt="Pasted content"
                      className="object-contain"
                      style={{
                        width: `${imageSize}%`,
                        maxWidth: "100%",
                        maxHeight: "100%",
                      }}
                    />
                  ) : (
                    <div className="text-center text-gray-500 w-full h-full flex flex-col items-center justify-center relative">
                      <div className="text-6xl mb-4">📋</div>
                      <div className="text-lg mb-4 hidden md:inline-block">
                        Paste an image here (Ctrl+V or Cmd+V)
                      </div>

                      <input
                        ref={hiddenInputRef}
                        type="text"
                        placeholder="Paste an image here"
                        onPaste={handleHiddenInputPaste}
                        onClick={(e) => {
                          const target = e.target as HTMLInputElement;
                          target.select();
                        }}
                        onFocus={(e) => {
                          const target = e.target as HTMLInputElement;
                          target.select();
                        }}
                        className="md:hidden w-40 px-3 py-2 text-center text-sm bg-white bg-opacity-20 border border-gray-300 border-opacity-50 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 focus:border-blue-500"
                        style={{ backdropFilter: "blur(10px)" }}
                      />
                    </div>
                  )
                ) : (
                  <span className="text-emojiSmall lg:text-emoji">{emoji}</span>
                )}
              </div>
            </div>

            <div className="text-center mt-8">
              <button
                type="button"
                onClick={handleDownloadImage}
                disabled={
                  isPreparingMobileExport ||
                  (mode === "ai" && !generatedImageUrl) ||
                  (mode === "paste" && !pastedImageUrl)
                }
                className="bg-blue-500 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded"
              >
                {isPreparingMobileExport
                  ? "Preparing image…"
                  : isMobileDevice()
                    ? "Open image"
                    : "Download PNG"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 text-xs md:text-sm text-slate-600 text-left md:text-center leading-5 ml-6 mr-4 mb-4">
        Made by&nbsp;
        <a
          className="border-b-2 border-slate-600 border-dotted"
          target="_blank"
          href="https://antoniocosentino.com"
          rel="noreferrer"
        >
          Antonio Cosentino
        </a>{" "}
        &copy; 2023 - 2026 ·{" "}
        <span className="hidden md:inline-block">&nbsp;</span>
        <a
          target="_blank"
          href="https://github.com/antoniocosentino/emojipic"
          rel="noreferrer"
        >
          <img
            alt="Github"
            className="w-3 h-3 inline-block bottom-0.5 relative"
            src="https://antoniocosentino.github.io/what-the-fout/github.svg"
          />
        </a>
        &nbsp;
        <a
          className="border-b-2 border-slate-600 border-dotted"
          target="_blank"
          href="https://github.com/antoniocosentino/emojipic"
          rel="noreferrer"
        >
          Source Code
        </a>{" "}
        · v.{packageJson.version}
      </div>
      {mobileExportImageUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Your emojipic image"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black p-4"
        >
          <div className="mb-4 flex w-full max-w-2xl items-center justify-between text-white">
            <p className="text-sm">Press and hold the image to save it to Photos.</p>
            <button
              type="button"
              onClick={() => {
                URL.revokeObjectURL(mobileExportImageUrl);
                setMobileExportImageUrl(null);
              }}
              className="ml-4 rounded border border-white px-3 py-1 font-bold"
            >
              Close
            </button>
          </div>
          <img
            src={mobileExportImageUrl}
            alt="Your emojipic"
            className="max-h-[calc(100vh-6rem)] max-w-full object-contain"
          />
        </div>
      )}
    </HelmetProvider>
  );
}

export default App;
